import { useTranslation } from 'react-i18next';
import type { IFinanceBudget } from '@interfaces/IFinance';
import { fmtUsdMillions, fmtUsdThousandsInt, fmtSignedThousands } from '../format';
import { KpiCard } from './KpiCard';
import { StatusPill } from './StatusPill';
import styles from './BudgetTab.module.css';

interface IBudgetTabProps {
  budget: IFinanceBudget;
}

export function BudgetTab({ budget }: IBudgetTabProps) {
  const { t } = useTranslation();
  const varianceTone = budget.kpis.varianceUsdK < 0 ? 'green' : 'red';

  return (
    <>
      <div className={styles.kpiRow}>
        <KpiCard
          value={fmtUsdMillions(budget.kpis.totalBudgetUsd)}
          label={t('finance.budget.totalBudget')}
          valueTone="navy"
        />
        <KpiCard
          value={fmtUsdMillions(budget.kpis.spentToDateUsd)}
          label={t('finance.budget.spentToDate')}
          valueTone="blue"
        />
        <KpiCard
          value={fmtSignedThousands(budget.kpis.varianceUsdK)}
          label={t('finance.budget.variance')}
          valueTone={varianceTone}
        />
        <KpiCard
          value={`${budget.kpis.budgetUtilisationPct.toFixed(1)}%`}
          label={t('finance.budget.budgetUtilization')}
          valueTone="navy"
        />
      </div>

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>{t('finance.budget.allocation')}</h2>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t('finance.budget.colCategory')}</th>
              <th>{t('finance.budget.colPhase')}</th>
              <th className={styles.alignRight}>{t('finance.budget.colBudget')}</th>
              <th className={styles.alignRight}>{t('finance.budget.colSpent')}</th>
              <th className={styles.alignRight}>{t('finance.budget.colVariance')}</th>
              <th className={styles.progressCol}>{t('finance.budget.colProgress')}</th>
              <th className={styles.alignCenter}>{t('finance.budget.colStatus')}</th>
            </tr>
          </thead>
          <tbody>
            {budget.categories.map((row) => (
              <tr key={row.category}>
                <td className={styles.bold}>{row.category}</td>
                <td className={styles.muted}>{row.phase}</td>
                <td className={styles.alignRight}>{fmtUsdThousandsInt(row.budgetK * 1000)}</td>
                <td className={styles.alignRight}>{fmtUsdThousandsInt(row.spentK * 1000)}</td>
                <td
                  className={`${styles.alignRight} ${
                    row.varianceK < 0 ? styles.varianceUnder : styles.varianceOver
                  }`}
                >
                  {fmtSignedThousands(row.varianceK)}
                </td>
                <td className={styles.progressCol}>
                  <div className={styles.progressTrack}>
                    <div
                      className={`${styles.progressFill} ${
                        row.status === 'over-budget'
                          ? styles.progressRed
                          : styles.progressGreen
                      }`}
                      style={{ width: `${Math.min(100, row.progressPct)}%` }}
                    />
                  </div>
                </td>
                <td className={styles.alignCenter}>
                  <StatusPill status={row.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </>
  );
}
